import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { ExternalLink, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI, showConfirmModal } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProjectSources = () => {
  const [sources, setSources] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [editingName, setEditingName] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const isAdmin = authAPI.isAdmin();

  const fetchSources = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/project-sources`);
      setSources(res.data.sources || []);
    } catch (err) {
      console.error('Error fetching sources:', err);
    }
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const resetForm = () => {
    setFormOpen(false);
    setEditingName(null);
    setInputValue('');
  };

  const handleSubmit = async () => {
    const name = inputValue.trim();
    if (!name) return;

    if (editingName) {
      if (name === editingName) {
        resetForm();
        return;
      }
      try {
        await axios.put(`${API_URL}/project-sources/${encodeURIComponent(editingName)}`, { name });
        toast.success('Source updated');
        resetForm();
        fetchSources();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error updating source');
      }
    } else {
      try {
        await axios.post(`${API_URL}/project-sources`, { name });
        toast.success('Source added');
        resetForm();
        fetchSources();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error adding source');
      }
    }
  };

  const handleEdit = (name) => {
    setEditingName(name);
    setInputValue(name);
    setFormOpen(true);
  };

  const handleDelete = async (name) => {
    if (!(await showConfirmModal(`Delete source "${name}"?`))) return;
    try {
      await axios.delete(`${API_URL}/project-sources/${encodeURIComponent(name)}`);
      toast.success('Source deleted');
      fetchSources();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting source');
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-base lg:text-lg text-[#5B8DB8] font-light" data-testid="sources-subtitle">Sources</h2>
          <h1 className="text-2xl lg:text-3xl text-gray-700 font-light italic" data-testid="sources-title">Settings</h1>
        </div>

        <div className="bg-white rounded shadow p-8" data-testid="sources-card">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
            {/* Left: Add Source button / form */}
            <div className="flex-shrink-0 w-full lg:w-72" data-testid="add-source-section">
              {!formOpen ? (
                <button
                  onClick={() => setFormOpen(true)}
                  className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white text-sm font-medium px-5 py-2 rounded transition-colors flex items-center gap-2"
                  data-testid="add-source-btn"
                >
                  <Plus size={16} /> Add Source
                </button>
              ) : (
                <div className="border border-gray-200 rounded p-4 bg-gray-50" data-testid="source-form">
                  <label className="text-[#5B8DB8] text-sm font-medium block mb-2" data-testid="source-label">
                    {editingName ? 'Edit Source' : 'New Source'}
                  </label>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSubmit();
                      if (e.key === 'Escape') resetForm();
                    }}
                    placeholder="Source name..."
                    autoFocus
                    className="border border-gray-300 rounded px-3 py-2 text-sm w-full focus:outline-none focus:border-[#6BADE0]"
                    data-testid="source-input"
                  />
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={handleSubmit}
                      disabled={!inputValue.trim()}
                      className="bg-[#5BC0DE] hover:bg-[#46b8da] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
                      data-testid="save-source-btn"
                    >
                      {editingName ? 'Save Changes' : 'Save Source'}
                    </button>
                    <button
                      onClick={resetForm}
                      className="border border-gray-300 hover:bg-gray-100 text-gray-600 text-sm px-4 py-1.5 rounded transition-colors"
                      data-testid="cancel-source-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Sources List */}
            <div className="flex-1" data-testid="sources-list-section">
              <table className="w-full max-w-lg text-sm" data-testid="sources-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left pb-2 font-semibold text-gray-700 italic" data-testid="sources-table-header">Source</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((src, idx) => (
                    <tr key={idx} className="border-b border-gray-100" data-testid={`source-row-${idx}`}>
                      <td className="py-3 text-gray-700">
                        <span data-testid={`source-name-${idx}`}>{src.name}</span>
                      </td>
                      <td className="py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(src.name)}
                            className="text-gray-500 hover:text-[#5B8DB8] border border-gray-300 rounded p-1 transition-colors"
                            data-testid={`source-edit-btn-${idx}`}
                          >
                            <ExternalLink size={14} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(src.name)}
                              className="text-[#DC3545] hover:text-[#C82333] border border-gray-300 rounded p-1 transition-colors hover:bg-red-50"
                              data-testid={`source-delete-btn-${idx}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProjectSources;
